import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTransactionService } from '../services/transaction.service';
import { CoinWithSymbol } from '../services/transaction.types';
import { useDebounce } from './useDebounce';

interface UseCoinSearchResult {
    loading: boolean;
    coins: CoinWithSymbol[];
    searchCoins: (query: string) => void;
    error: unknown;
    hasSearched: boolean;
    resetSearch: () => void;
}

export const useCoinSearch = (initialCoins: CoinWithSymbol[] = []): UseCoinSearchResult => {
    // Track if we've made at least one search request
    const [hasSearched, setHasSearched] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const transactionService = useTransactionService();

    const resetSearch = () => {
        setHasSearched(false);
        setSearchTerm('');
    };

    const searchCoins = useDebounce((query: string) => {
        setSearchTerm(query);
        if (!hasSearched) setHasSearched(true);
    }, 300);

    const {
        data: coins = initialCoins,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['coinSearch', searchTerm],
        queryFn: () => transactionService.getCoins(searchTerm),
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        enabled: searchTerm.length > 0,
    });

    return {
        loading: isLoading,
        coins,
        searchCoins,
        error,
        hasSearched,
        resetSearch,
    };
};
